import { useState, type ChangeEvent, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'

interface FormData {
  name: string
  email: string
  phone: string
  message: string
}

interface FormErrors {
  name?: string
  email?: string
  message?: string
}

const highlights = [
  { label: 'Average response time', value: '< 24 hours' },
  { label: 'Connection hours', value: 'Every day, 9a - 4p PT' },
]

const ContactForm = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [isLoading, setIsLoading] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'error'>('idle')
  const [submitMessage, setSubmitMessage] = useState('')

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!validateForm()) return

    setIsLoading(true)
    setSubmitStatus('idle')
    setSubmitMessage('')

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to send email')
      }

      navigate('/thank-you')
    } catch (error) {
      setSubmitStatus('error')
      setSubmitMessage(error instanceof Error ? error.message : 'An error occurred. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section id="contact" className="relative isolate overflow-hidden bg-[#cdfbf1] py-28 scroll-mt-24">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.9),_rgba(205,251,241,0.6))]" />
      <div className="container relative z-10 mx-auto px-6">
        <div className="grid gap-10 lg:grid-cols-[0.7fr_1fr]">
          <div className="rounded-3xl border border-slate-200/70 bg-white/90 p-10 shadow-[0_25px_60px_rgba(15,23,42,0.08)]">
            <p className="text-xs uppercase tracking-[0.5em] text-teal-600">Concierge support</p>
            <h2 className="mt-4 text-4xl font-semibold text-slate-900">Get in touch with Andrea</h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Prefer a voice memo, FaceTime, or email thread? Leave a note below and Andrea will help you choose the best way to connect.
            </p>
            <dl className="mt-8 grid gap-4">
              {highlights.map((item) => (
                <div key={item.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <dt className="text-xs uppercase tracking-[0.3em] text-slate-500">{item.label}</dt>
                  <dd className="text-lg font-semibold text-slate-900">{item.value}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-8 space-y-3 text-sm text-slate-600">
              <p><span className="font-semibold text-slate-900">Email:</span> info@andreavillalobos.com</p>
              <p><span className="font-semibold text-slate-900">Location:</span> Los Angeles, California</p>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="rounded-3xl border border-white/60 bg-white/90 p-8 backdrop-blur-xl shadow-[0_35px_80px_rgba(15,23,42,0.12)]"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-slate-900 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className={`w-full rounded-2xl border-2 px-4 py-3 text-base transition focus:outline-none ${
                    errors.name ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-white focus:border-teal-400'
                  }`}
                  disabled={isLoading}
                />
                {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-900 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@email.com"
                  className={`w-full rounded-2xl border-2 px-4 py-3 text-base transition focus:outline-none ${
                    errors.email ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-white focus:border-teal-400'
                  }`}
                  disabled={isLoading}
                />
                {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-slate-900 mb-2">
                  Phone <span className="text-xs text-slate-500">optional</span>
                </label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className="w-full rounded-2xl border-2 border-slate-200 bg-white px-4 py-3 text-base transition focus:border-teal-400 focus:outline-none"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-slate-900 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Share your goals or the kind of accountability support you are looking for."
                  rows={5}
                  className={`w-full resize-none rounded-2xl border-2 px-4 py-3 text-base transition focus:outline-none ${
                    errors.message ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-white focus:border-teal-400'
                  }`}
                  disabled={isLoading}
                />
                {errors.message && <p className="mt-1 text-sm text-red-500">{errors.message}</p>}
                <p className="mt-2 text-xs text-slate-500">
                  Please do not include medical history, diagnoses, medication details, or urgent health concerns.
                </p>
              </div>

              {submitStatus === 'error' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  {submitMessage}
                </motion.div>
              )}

              <motion.button
                type="submit"
                whileHover={{ scale: isLoading ? 1 : 1.01 }}
                whileTap={{ scale: isLoading ? 1 : 0.99 }}
                disabled={isLoading}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-400 px-6 py-3 text-base font-semibold text-white shadow-lg transition disabled:opacity-70"
              >
                {isLoading ? (
                  <>
                    <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send message'
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default ContactForm
